import { isNil } from 'lodash';
import { useContext, useMemo } from 'react';
import { Menu, Submenu, useRootMenuContext } from '@shared/components/Menu';
import { ShareQRCode } from '@shared/components/qrCodes';
import {
  copyToClipboard,
  useHandleCopyQr,
  useSharedCardLinkState,
} from '@shared/scenes/cards/CardActionMenu/shareQrCodeOperations';
import { CardChannelContext } from '@shared/scenes/cards/context';
import { useCardDefinition } from '@shared/scenes/cards/hooks';
import { i18n } from '@shared/util/i18n';
import { usePermission } from '@shared/util/permissions/hooks';
import styles from './ShareSubmenu.module.css';

interface Props {
  entityId: string;
}

// TODO: Refactor to not use card definition
// https://app.shortcut.com/coast/story/75259
export function ShareSubmenu({ entityId }: Props) {
  const { align } = useRootMenuContext();
  const channel = useContext(CardChannelContext);
  const sharedCardLinkPermissions = usePermission({
    action: 'manage',
    subject: 'SharedCardLink',
    channelId: channel?.id,
  });
  const { cardDefinition } = useCardDefinition(channel?.workflowTemplateId);
  const nocodeReadonlyPublicTemplate = cardDefinition?.templates?.find(uiTemplate => {
    return uiTemplate.template.context === 'nocode-public-readonly';
  });

  const cardHost = getCardHost();
  const privateCardUrl = useMemo(() => {
    return `${cardHost}/${channel?.slug}/cards/${entityId}`;
  }, [cardHost, channel?.slug, entityId]);

  const { showPublicQrCode, publicLink, onToggleShowPublicQrCode } = useSharedCardLinkState({
    channelId: channel?.id,
    type: 'VIEW_CARD',
    viewTemplateId: nocodeReadonlyPublicTemplate?.template?.slug,
    cardId: entityId,
    disabled: sharedCardLinkPermissions.enabled,
  });

  const copyQr = useHandleCopyQr();
  const hasNoCodeReadonlyPublicTemplate = !isNil(nocodeReadonlyPublicTemplate);

  return (
    <Submenu align={align}>
      <Submenu.Trigger>
        <Menu.Item label={i18n('common.share')} />
      </Submenu.Trigger>
      <Submenu.Content testID="share-card-sheet" className={styles.shareSubmenuContent}>
        <ShareQRCode
          privateShare={{
            url: privateCardUrl,
            onCopyQr: copyQr,
            onCopyLink: copyToClipboard,
          }}
          canCreatePublicCode={sharedCardLinkPermissions.enabled && hasNoCodeReadonlyPublicTemplate}
          hasPublicQrCode={showPublicQrCode}
          publicUrl={publicLink?.url}
          onSwitchPublic={onToggleShowPublicQrCode}
          onCopyPublicQr={copyQr}
          onCopyPublicLink={copyToClipboard}
        />
      </Submenu.Content>
    </Submenu>
  );
}

function getCardHost() {
  const appUrl = import.meta.env.VITE_APP_URL;
  if (import.meta.env.DEV && appUrl.includes('localhost')) {
    return 'https://staging.coastapp.com';
  } else {
    return appUrl;
  }
}
